import { DegreeEvent_AssignmentProgressState as AssignmentProgressState } from '__generated__/graphql-types';
import { flatMap, sortBy } from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';

import { momentWithUserTimezone } from 'js/utils/DateTimeUtils';

import type {
  CalendarEventType,
  CalendarEventsMap,
  CalendarEventsMapByFilter,
  CalendarEventsType,
} from 'bundles/degree-home/components/v2/types/DegreeHomeContextTypes';
import type { FilterOptionValuesType } from 'bundles/degree-home/components/v3-dpm/components/Calendar';
import {
  calendarListFooterId,
  filterOptionValues,
} from 'bundles/degree-home/components/v3-dpm/components/Calendar/constants/constants';
import { ItemTypeNames } from 'bundles/item/types/ItemType';

export const toCamelCase = (str: string) => {
  return str.replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase()).replace(/^\w/, (c) => c.toLowerCase());
};

/**
 * @returns date string in ISO format in the user's timezone, at the start of the day'
 * ex:
 * We want to use a date format that is consistent across different locales
 * The return value of this function should be transformable into a date/moment object
 * @param date
 */
export const getCalendarKey = (date: Moment | Date | string): string => {
  // we want all calendar items, that have the same date in the user's timezone, to be grouped together
  const normalizedDate = momentWithUserTimezone(date).startOf('day');

  // ISO is a standard date format that is parsed/understood correctly by moment, Date, and it matches the gRPC protobuf spec
  return normalizedDate.toISOString();
};

/**
 * @returns an object keyed by filter value, storing a map of calendar events.
 * The calendar events are keyed by date, storing a list of calendar items on that date
 */
export const getCalendarEventsMap = (events: CalendarEventsType): CalendarEventsMapByFilter => {
  return events.reduce<CalendarEventsMapByFilter>(
    (itemMap, item) => {
      const currMap = { ...itemMap };
      let key;
      let isDismissible = false;

      if (item.__typename === 'DegreeEvent_AcademicEvent') {
        key = getCalendarKey(item.eventDate);

        if (key in currMap[filterOptionValues.Deadlines]) {
          currMap[filterOptionValues.Deadlines][key].push(item);
        } else {
          currMap[filterOptionValues.Deadlines][key] = [item];
        }
        if (key in currMap[filterOptionValues.All]) {
          currMap[filterOptionValues.All][key].push(item);
        } else {
          currMap[filterOptionValues.All][key] = [item];
        }
      } else if (item.__typename === 'DegreeEvent_AssignmentEvent') {
        // Do not display Practice Assignment items
        if (toCamelCase(item.assignmentType) === ItemTypeNames.ungradedAssignment) {
          return currMap;
        }

        key = getCalendarKey(item.dueDate);
        isDismissible = !item.isDismissed;
        if (!(item.progressState === AssignmentProgressState.Overdue && item.isDismissed)) {
          if (key in currMap[filterOptionValues.Deadlines]) {
            currMap[filterOptionValues.Deadlines][key].push(item);
          } else {
            currMap[filterOptionValues.Deadlines][key] = [item];
          }
        }
      } else if (item.__typename === 'DegreeEvent_LiveEvent') {
        key = getCalendarKey(item.startTime);
        isDismissible = !item.isDismissed;

        if (key in currMap[filterOptionValues.LiveEvents]) {
          currMap[filterOptionValues.LiveEvents][key].push(item);
        } else {
          currMap[filterOptionValues.LiveEvents][key] = [item];
        }
      }

      if (
        item.__typename === 'DegreeEvent_AssignmentEvent' &&
        item.progressState === AssignmentProgressState.Overdue &&
        key
      ) {
        if (key in currMap[filterOptionValues.Overdue]) {
          currMap[filterOptionValues.Overdue][key].push(item);
        } else {
          currMap[filterOptionValues.Overdue][key] = [item];
        }
      }

      if (isDismissible && key) {
        if (key in currMap[filterOptionValues.All]) {
          currMap[filterOptionValues.All][key].push(item);
        } else {
          currMap[filterOptionValues.All][key] = [item];
        }
      }

      return currMap;
    },
    {
      [filterOptionValues.All]: {},
      [filterOptionValues.Deadlines]: {},
      [filterOptionValues.LiveEvents]: {},
      [filterOptionValues.Overdue]: {},
    }
  );
};

/**
 * @returns calendar evemts with overdue items prepended to today's events list
 */
export const getCalendarEventsWithOverdue = (
  calendarEvents: CalendarEventsMapByFilter | null,
  visibleCalendarEvents: CalendarEventsMap,
  filter: FilterOptionValuesType
) => {
  if (!calendarEvents) return {};
  const calendarEventsItems = Object.assign({}, visibleCalendarEvents);

  const todaysDate = getCalendarKey(momentWithUserTimezone());
  const overdueItems = flatMap(calendarEvents[filterOptionValues.Overdue], (items) => items).filter(
    (item) => item.__typename === 'DegreeEvent_AssignmentEvent' && !item.isDismissed
  );

  // If overdue items exist, prepend them to today's item list
  if (overdueItems.length > 0 && (filter === filterOptionValues.All || filter === filterOptionValues.Deadlines)) {
    if (todaysDate in calendarEventsItems) {
      const todaysItems = [
        // Don't duplicate overdue items from today
        ...overdueItems.filter(
          (item) =>
            getCalendarKey(
              // @ts-expect-error DateTime: string
              momentWithUserTimezone(item.__typename === 'DegreeEvent_AssignmentEvent' && item.dueDate) || ''
            ) !== todaysDate
        ),
        ...calendarEventsItems[todaysDate],
      ];
      // place overdue items in an overdue section
      calendarEventsItems.overdue = todaysItems.filter(
        (item: CalendarEventType) =>
          item.__typename === 'DegreeEvent_AssignmentEvent' && item.progressState === AssignmentProgressState.Overdue
      );
      // only leave non-overdue items in today's list
      calendarEventsItems[todaysDate] = todaysItems.filter(
        (item: CalendarEventType) =>
          (item.__typename === 'DegreeEvent_AssignmentEvent' &&
            item.progressState !== AssignmentProgressState.Overdue) ||
          item.__typename === 'DegreeEvent_AcademicEvent' ||
          item.__typename === 'DegreeEvent_LiveEvent'
      );
    } else {
      calendarEventsItems.overdue = overdueItems;
    }
  }
  return calendarEventsItems;
};

/**
 * @returns string of the next date where a calendar item exists
 */
export const getNearestDate = (dateInFocus: Moment, calendarDates: string[]): string => {
  // if calendar is empty, return the "no more items" footer
  if (calendarDates.length === 0) {
    return calendarListFooterId;
  }

  // if overdue section exists and dateInFocus is today, scroll to overdue section
  const todaysDate = getCalendarKey(momentWithUserTimezone());
  if (
    calendarDates.includes(filterOptionValues.Overdue) &&
    dateInFocus.startOf('day').diff(moment(todaysDate), 'days') === 0
  ) {
    return filterOptionValues.Overdue;
  }

  const dateToCheckFor = getCalendarKey(dateInFocus);

  // first check to see if the date is a key in the items map
  if (calendarDates.includes(dateToCheckFor)) {
    return dateToCheckFor;
  }

  // TODO - dcuccias: this needs to be pulled out so it doesn't execute every time
  const sortedCalendarDates = calendarDates.sort((d1, d2) => moment(d1).diff(moment(d2)));

  // find the first date immediately after dateToCheckFor
  const nearestDate = sortedCalendarDates.find((date) => {
    const diff = moment(date).diff(moment(dateToCheckFor), 'days');
    return diff > 0;
  });

  // if nearestDate is undefined while date in focus is before today
  const noNearestDate = !nearestDate && dateInFocus.startOf('day').diff(moment(todaysDate), 'days') < 0;
  // nearestDate is today
  const nearestDateIsToday = nearestDate === todaysDate;
  // nearestDate is in the future and date in focus is between today and nearestDate
  const nearestDateIsFuture =
    dateInFocus.startOf('day').diff(moment(todaysDate), 'days') < 0 &&
    moment(nearestDate).startOf('day').diff(moment(todaysDate), 'days') > 0;
  if (
    calendarDates.includes(filterOptionValues.Overdue) &&
    (noNearestDate || nearestDateIsToday || nearestDateIsFuture)
  ) {
    return filterOptionValues.Overdue;
  }

  // if there are no calendar items after dateInFocus, return the "no more items" footer
  return nearestDate || calendarListFooterId;
};

export const scrollToNearestDateEvents = (dateInFocus: Moment, calendarListItems: CalendarEventsMap) => {
  const nearestDate = getNearestDate(dateInFocus, Object.keys(calendarListItems));
  const nearestDateElement = document.getElementById(nearestDate);
  const container = document.getElementById('calendarItemsContainer');
  if (nearestDateElement && container) {
    container.scrollTo({ top: nearestDateElement.offsetTop - container.offsetTop, behavior: 'smooth' });
  }
};

/**
 * @returns array of sorted calendar dates with correctly positioned overdue section
 */
export const getOrderedCalendarListItems = (dateInFocus: Moment, calendarDates: string[]) => {
  let calendarOrderedListItems = sortBy(calendarDates).filter((date) => date !== filterOptionValues.Overdue);

  // order the dates and position the overdue section accordingly
  if (calendarDates.includes(filterOptionValues.Overdue)) {
    const nearestDate = getNearestDate(dateInFocus, calendarOrderedListItems);
    const todaysDate = getCalendarKey(momentWithUserTimezone());

    if (calendarOrderedListItems.includes(todaysDate)) {
      // if there are calendar items today, always position overdue section before today
      calendarOrderedListItems.splice(calendarOrderedListItems.indexOf(todaysDate), 0, filterOptionValues.Overdue);
    } else if (nearestDate === calendarListFooterId) {
      // if there are overdue items, but no nearestDate. position overdue section at the end
      calendarOrderedListItems.splice(calendarOrderedListItems.length, 0, filterOptionValues.Overdue);
    } else {
      // if there are no items today and there are overdue items, insert todays date and replace it with overdue after sorting
      calendarOrderedListItems.splice(0, 0, todaysDate);
      calendarOrderedListItems = sortBy(calendarOrderedListItems);
      calendarOrderedListItems[calendarOrderedListItems.indexOf(todaysDate)] = filterOptionValues.Overdue;
    }
  }
  return calendarOrderedListItems;
};
